import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import {parseJSON, stringifyJSON} from '../Common/json';
import {javascriptHint} from '../Common/javaScriptHint';

const createOptions = (el, defaultValue) => {
  const readOnly = el.classList.contains('read-only');
  let value = el.value || defaultValue || '';

  if (el.classList.contains('json-editor')) {
    if (typeof value !== 'string') {
      value = stringifyJSON(value, '', 2);
    } else if (!isEmpty(value)) {
      value = stringifyJSON(parseJSON(value), '', 2);
    }

    const settings = {
      lint: !isEmpty(value),
      mode: 'application/json',
      value,
      foldGutter: true,
      gutters: ['CodeMirror-lint-markers', 'CodeMirror-foldgutter'],
    };

    if (readOnly) {
      return {
        ...settings,
        readOnly,
        lineWrapping: true,
      };
    }

    return settings;
  } else if (el.classList.contains('js-editor')) {
    return {
      lint: {
        asi: true,
        expr: true,
        esversion: 6,
      },
      readOnly,
      mode: 'javascript',
      value,
      hintOptions: {hint: javascriptHint},
      extraKeys: {'Ctrl-Space': 'autocomplete'},
    };
  }

  return {};
};

const unableToLoadEditor = el => {
  el.parentNode.querySelectorAll('.ui.loader').forEach(loader => loader.remove());

  const errorMessage = document.createElement('div');
  errorMessage.className = 'ui message error visible';
  errorMessage.innerHTML = 'Sorry! We are temporarily unable to load the editor. Please refresh this page. If you continue to see this error or have issues in general, <a href="/contact">contact us</a>.';
  el.replaceWith(errorMessage);
};

export const javaScriptEditor = (el, defaultValue) => {
  const loader = document.createElement('div');
  loader.className = 'ui active inline tiny loader';
  loader.style.marginRight = '1em';
  el.parentNode.insertBefore(loader, el.nextSibling);

  import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
  .then(module =>
      module.codeEditor({
        callbackFn: editor => {
          if (el.classList.contains('json-editor') || el.classList.contains('js-editor')) {
            editor.on('blur', codeMirror => {
              let annotations = [];

              if (el.classList.contains('json-editor')) {
                codeMirror.setOption('lint', !isEmpty(codeMirror.getValue()));

                const getAnnotations = codeMirror.getHelper(
                    module.CodeMirror.Pos(0, 0), 'lint');
                annotations = getAnnotations(codeMirror.getValue(),
                    codeMirror.state?.lint?.linterOptions, codeMirror);

                const oldValue = parseJSON(el.value);
                const newValue = parseJSON(codeMirror.getValue());
                if (!isEqual(oldValue, newValue) && isEmpty(annotations)) {
                  el.value = stringifyJSON(newValue);
                }
              } else if (el.classList.contains('js-editor')) {
                const getAnnotations = codeMirror.getHelper(
                    module.CodeMirror.Pos(0, 0), 'lint');
                annotations = getAnnotations(codeMirror.getValue(),
                    codeMirror.state?.lint?.linterOptions, codeMirror);

                const oldValue = el.value.trim();
                const newValue = codeMirror.getValue().trim();
                if (!isEqual(oldValue, newValue) && isEmpty(annotations)) {
                  el.value = newValue;
                }
              }
            });
          }

          const wrapperElement = editor.getWrapperElement();
          wrapperElement.classList.add('ui', 'segment', 'fitted');
          wrapperElement.style.marginBottom = '0';

          el.parentNode.querySelectorAll('.ui.loader').forEach(loader => loader.remove());
          el.parentNode.insertBefore(wrapperElement, el.nextSibling);

          editor.refresh();
        },
        errorFn: () => unableToLoadEditor(el),
        options: createOptions(el, defaultValue),
      })
  ).catch(() => {
    unableToLoadEditor(el);
  });
};

document.addEventListener('DOMContentLoaded', () =>
    document.querySelectorAll('textarea.js-editor,textarea.json-editor').
    forEach(el => javaScriptEditor(el)));